import React, { useEffect, useState } from 'react';

import { locationForRoute } from 'files/routes.js';
import { CHANGED, isComplete, isInProgress, load } from 'files/quick-look-storage.js';

/* The call to action at the foot of an article, which is not the same ask for
   everybody who reaches it.

   Every article used to end on the same invitation regardless of who was
   reading. For a stranger that is exactly right. For someone who finished a
   Quick Look last week it is the site failing to recognise them, and pitching
   somebody the thing they have already done reads worse than saying nothing at
   all — it tells them nobody is really on the other end.

   Four states, because the funnel has four positions and the last one is the
   easy one to forget:

   - nothing yet          the article's own closing line, written for this piece
   - part-way through     pick it up, do not start again
   - finished             they have a read; the next step is a person
   - already reached out  say so and stop asking

   The fourth matters most. Someone who has sent their details and is still
   reading is the warmest visitor on the site, and asking them to get in touch
   on every page they open is how that goodwill gets spent.

   The article's own callout is kept for the cold state and only the cold state.
   It was written to land on someone meeting the idea for the first time, and it
   is better at that than anything generic. Past that point the reader knows what
   we do and the honest thing is to talk about where they actually are. */

/* Where this reader stands, and what to call the way in.

   Exported because the callout at the foot of a piece is not the only place that
   asks. The contents rail carries the same invitation halfway up the page, and
   it was asking every reader to take a Quick Look regardless of whether they had
   already taken one. Somebody who finished the tool last week and came back to
   read was being told, in a fixed panel that follows them down the page, to go
   and do the thing they had done. Two asks that disagree about who the reader is
   are worse than one, because now the site is visibly not paying attention.

   The state lives here and the wording is returned with it. Both are needed in
   both places, and the rail is a narrow column where the callout is a paragraph,
   so each gets a length that fits rather than one string stretched across both. */
export const useQuickLookAsk = () => {

    // read after mount and again on change: the Quick Look runs in a modal over
    // this page, so someone can finish one without the article ever remounting
    const [record, setRecord] = useState(null);

    useEffect(() => {
        const sync = () => setRecord(load());
        sync();
        window.addEventListener(CHANGED, sync);
        return () => window.removeEventListener(CHANGED, sync);
    }, []);

    // they submitted the form on their read, so we already have their details
    const reached = Boolean(record && record.email);
    const finished = isComplete(record);
    const started = isInProgress(record);

    if(reached) {
        return { label: 'Read yours again', rail: 'Read your Quick Look again', reached, finished, started, record };
    }
    if(finished) {
        // the same hand-off the callout makes, so the modal opens on the form
        return { intent: 'contact', label: 'Talk it through with someone', rail: 'Talk it through with someone', reached, finished, started, record };
    }
    if(started) {
        return { label: 'Pick up where you left off', rail: 'Pick up where you left off', reached, finished, started, record };
    }
    return { label: 'Take a Quick Look', rail: 'Take a Quick Look at your scenario', reached, finished, started, record };
}

/* The invitation as the contents rail renders it: one row of the same list as
   every section above it, worded for where the reader actually is. */
export const QuickLookRailLink = ({ utils }) => {

    const ask = useQuickLookAsk();

    const onOpen = evt => {
        evt.preventDefault();
        utils.navigation.set({ view: 'quick-look', ...(ask.intent ? { intent: ask.intent } : {}) });
    }

    return (
        <li className={'ar-rail-aside'}>
            <a href={locationForRoute({ view: 'quick-look' })} onClick={onOpen}>
                {/* one span, so the arrow sits at the end of the wrapped label
                    rather than as a second flex child pinned to the far edge */}
                <span>{ask.rail} <span className={'dl-arrow'}>→</span></span>
            </a>
        </li>
    )
}

const QuickLookCta = ({ callout, utils }) => {

    const ask = useQuickLookAsk();
    const record = ask.record;

    const go = (view, props) => evt => {
        evt.preventDefault();
        utils.navigation.set({ view: view, ...props });
    }

    const { finished, reached, started } = ask;

    /* Named from the read they were actually shown, so the line is about their
       business rather than about the tool. */
    const opening = finished && record.read && record.read.areas && record.read.areas.length > 0
        ? record.read.areas[0].title.toLowerCase().replace(/\.$/, '')
        : null;

    if(reached) {
        return (
            <aside className={'ar-callout is-quiet'}>
                <p>
                    You’ve finished your Quick Look and we have your information. One of us will be in touch. In the meantime everything here is yours to read.
                </p>
                <a className={'dl-quiet'} href={locationForRoute({ view: 'quick-look' })} onClick={go('quick-look')}>
                    Read yours again <span className={'dl-arrow'}>→</span>
                </a>
            </aside>
        )
    }

    if(finished) {
        return (
            <aside className={'ar-callout'}>
                <p>
                    You’ve already finished your Quick Look
                    {opening ? `, and the clearest opening in it was ${opening}` : ''}. What’s
                    actually worth doing about it, in what order, and how it fits with the tools
                    you already run is the part a person has to look at. That’s a conversation,
                    and it’s free.
                </p>
                {/* Carries what the click was for. Somebody who pressed this
                    wants the conversation, not another read of a read they have
                    already seen, so the modal opens on the form rather than
                    making them scroll the whole piece to find it. */}
                <a
                className={'dl-btn'}
                href={locationForRoute({ view: 'quick-look' })}
                onClick={go('quick-look', { intent: 'contact' })}>
                    Talk it through with someone <span className={'dl-arrow'}>→</span>
                </a>
            </aside>
        )
    }

    if(started) {
        return (
            <aside className={'ar-callout'}>
                <p>
                    You started a Quick Look and stopped part-way through. Your answers are still
                    here, so it picks up where you left off rather than starting again, and the
                    read at the end is yours either way.
                </p>
                <a className={'dl-btn'} href={locationForRoute({ view: 'quick-look' })} onClick={go('quick-look')}>
                    Pick up where you left off <span className={'dl-arrow'}>→</span>
                </a>
            </aside>
        )
    }

    return (
        <aside className={'ar-callout'}>
            <p>{callout}</p>
            <a className={'dl-btn'} href={locationForRoute({ view: 'quick-look' })} onClick={go('quick-look')}>
                Take a Quick Look <span className={'dl-arrow'}>→</span>
            </a>
        </aside>
    )
}

export default QuickLookCta;
